import { Typography, Grid, Theme } from "@mui/material";
import { useMemo, ReactNode } from "react";
import { Target } from "@labs/shared/types/Target.ts";
import { AnalyzedPlan } from "@labs/shared/types/AnalyzedPlan.ts";
import { LabsCard } from "./LabsCard.tsx";
import { SystemStyleObject } from "@mui/system";
import { CostScoreDisplay } from "./CostScoreDisplay";

type DashboardHighlightProps = {
  target: Target;
  analyzedPlan: AnalyzedPlan;
  compactDisplay: boolean;
};

type CardItem = {
  label: string;
  value: ReactNode;
  styles: SystemStyleObject<Theme>;
};

export const DashboardHighlight = ({
  target,
  analyzedPlan,
  compactDisplay,
}: DashboardHighlightProps) => {
  const cardItems: CardItem[] = useMemo(() => {
    const plannedReduction = -Math.round(analyzedPlan.planAbsoluteImpact);
    const isAboveTarget = plannedReduction >= target.goal;

    const plannedReductionStyles = {
      border: `1px solid ${isAboveTarget ? "green" : "error"}`,
      backgroundColor: isAboveTarget ? "lightgreen" : "lightcoral",
    };
    const plannedImpactLabel =
      plannedReduction > 0
        ? `Planned Reduction vs ${target.baselineYear}`
        : `Planned Increase vs ${target.baselineYear}`;

    const plannedImpactLabelCompact =
      plannedReduction > 0 ? "Plan (Red.)" : "Plan (Inc.)";

    return [
      {
        label: compactDisplay ? "Year" : "Target Year",
        value: target.targetYear,
        styles: {},
      },
      {
        label: compactDisplay
          ? "Target"
          : `Target Reduction vs ${target.baselineYear}`,
        value: `${target.goal}%`,
        styles: {},
      },
      {
        label: compactDisplay ? plannedImpactLabelCompact : plannedImpactLabel,
        value: `${Math.abs(plannedReduction)}%`,
        styles: plannedReductionStyles,
      },
      {
        label: compactDisplay ? "Inv." : "Investment",
        value: <CostScoreDisplay costScore={analyzedPlan.costScore} />,
        styles: {},
      },
    ];
  }, [
    analyzedPlan.planAbsoluteImpact,
    analyzedPlan.costScore,
    target.goal,
    target.baselineYear,
    target.targetYear,
    compactDisplay,
  ]);

  return (
    <>
      {cardItems.map((item) => (
        <Grid
          key={item.label}
          size={6}
          sx={{
            alignItems: "stretch",
            display: "flex",
          }}
          component="dl" // according to mdn, this should still work as an ancestor and not a direct child of dt/dd
        >
          <LabsCard
            compactDisplay={compactDisplay}
            sx={{
              ...item.styles,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="body2"
              textAlign="center"
              component="dt"
              id={`dt__${item.label}`}
            >
              {item.label}
            </Typography>
            <Typography
              variant={compactDisplay ? "body1" : "h6"}
              textAlign="center"
              component="dd"
              aria-labelledby={`dt__${item.label}`}
            >
              {item.value}
            </Typography>
          </LabsCard>
        </Grid>
      ))}
    </>
  );
};
